/**
 * Every write, as a mutation against the one store.
 *
 * Each of these wraps the `db.ts` function that already existed — the writes
 * themselves have not moved and their signatures have not changed — and adds
 * the two halves `useAction` never had:
 *
 * - **`onMutate` patches the cache.** The row leaves the feed, the heart
 *   fills, the tick goes on, the badge moves: all on the tap, none of it
 *   waiting for a round trip. This is what `useRemovals` was, generalised —
 *   that overlay only ever covered removals, and only on the screen doing the
 *   removing, which is why saving from Home's rail left the tab badge showing
 *   yesterday's number (`revision.ts`'s own opening complaint).
 * - **`onError` puts back exactly what was there.** The snapshot is the
 *   previous cache entry, whole, rather than a set of ids to un-hide. A
 *   rollback that reconstructs is a rollback that can be wrong.
 *
 * ## What gets invalidated, and what does not
 *
 * Nothing refetches the feed. `useAction` re-pulled every screen's whole query
 * after every write, which for Feed meant 2,400 rows to record that one row
 * had been saved.
 *
 * Beyond that the rule is: a write whose optimistic patch **is** the server's
 * outcome invalidates nothing, because a refetch could only confirm what the
 * client already wrote. A write that creates a row invalidates that row's key
 * and nothing else, because the real id and the server's defaults are things
 * only the server knows — a temporary id cannot be edited or deleted, and
 * leaving one in the cache is the one-value-two-jobs bug wearing a uuid.
 *
 * ## WP 1.7: what makes a write survive going offline
 *
 * `store.ts` persists the whole `QueryClient`, mutations included, and its
 * `sanitize()` explains why a paused mutation's `context` (this file's own
 * `{ undo }` closures) never makes it into that snapshot. What is missing
 * from a snapshot on its own is a `mutationFn` — `dehydrateMutation` carries
 * only `mutationKey`, `state`, `meta` and `scope`, never the function, so a
 * mutation rebuilt from a persisted, still-paused state has nothing to run
 * unless the client already knows one for that key. `durableWrite` registers
 * that once per write, at module load, via `setMutationDefaults` — the same
 * key a rebuilt mutation carries is how it finds its way back to the right
 * function.
 */
import { useMutation, useMutationState, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  acknowledgeAlert, addCommitment, addFlight, addItem, addItems, addItemForEntry, addList, addNote, addStay,
  addVisitor, archiveList, bumpUsual, changeListKind, deleteList, dismissEntry, dismissLead, editNote,
  followVenue, forEachEntry, forgetEntry, markWent, markWrong, newRun, pinList, promoteLead,
  removeCommitment, removeFlight, removeItem, removeNote, removeStay, removeVisitor, renameList,
  reorderItems, reorderLists, resetList, restoreDismissed, saveEntry, scheduleEntry,
  setItemDone, completeTask, clearDone, moveLine, setItemFields, setNoteDone, unfollowVenue, updateCommitment, updateFlight,
  setTasteVector, updateItem, updateProfile, updateStay, updateVisitor, type ListItemFields, type NoteKind,
} from './db'
import { dayIn, hereZone } from './zone'
import { useAuth } from './auth'
import type { Profile, ProfilePatch } from './profileDefaults'
import { queryClient } from './store'
import { nextDue } from './taskRules'
import { supabase } from './supabase'
import { toast } from '../ui/Toast'
import { activeTasteKey, profileKey, type ActiveTaste, type EntryStateRow, type AdminVenue } from './queries'
import type { VectorJson } from './tasteForm'
import type {
  AppNote, Commitment, Flight, Lead, LeaveDay, List, ListItem, ListKind, Stay, StayKind, Visitor,
} from './types'

/** What a save or a dismissal records about the row. Read off `db.ts` rather
 *  than restated, so the two cannot drift. */
type Features = Parameters<typeof saveEntry>[1]

/**
 * A Supabase write, as a promise that rejects.
 *
 * The `db.ts` functions return their error rather than throwing it — the
 * shape `useAction` read — and TanStack Query decides "did this fail" from a
 * rejection. Nothing here swallows anything, which was `useAction`'s own
 * reason for existing.
 *
 * The thrown `Error` carries `status` off the same response, read by
 * `shouldRetryWrite` below. It is never read off `err` itself: PostgREST's
 * `error.code` is a Postgres error code (`42501`, `PGRST301`, …), not an HTTP
 * one — `status` is the sibling field the response actually carries it in.
 * A request that never reached the server at all — offline, DNS, a dropped
 * connection — is not a gap here either: `postgrest-js` catches that itself
 * and answers with `status: 0` rather than letting the promise reject, so
 * `shouldRetryWrite` treats `0` the same as `undefined`, and both the same as
 * "never reached the server."
 */
async function ok<T>(work: PromiseLike<T>): Promise<T> {
  const res = await work
  const err = (res as { error?: { message?: string } | null } | null | undefined)?.error
  if (err) {
    const status = (res as { status?: number } | null | undefined)?.status
    throw Object.assign(new Error(err.message ?? String(err)), { status })
  }
  return res
}

/**
 * Whether a write that just failed should try again once it can.
 *
 * `store.ts`'s `retry: 0` is the default for every mutation, deliberately:
 * `addCommitment` and `addItem` are inserts, and a retried insert is a
 * duplicate row. This overrides it, but only for the one shape of failure a
 * retry cannot duplicate anything for — the request never reached the server
 * (`status` `0` or `undefined`: offline, or the fetch itself threw) or the
 * server never finished handling it (a 5xx). A 4xx is the server having read
 * the request and said no; asking again would only get the same no, so this
 * returns `false` and the write settles as an error immediately, for
 * `useWriteError` to read off the mutation instead of retrying it.
 *
 * Capped at one extra attempt (`failureCount > 0` stops it) rather than the
 * infinite `true` an offline queue usually wants: `networkMode: 'offlineFirst'`
 * pauses this exact attempt for as long as the device is actually offline —
 * `resumePausedMutations` on reconnect or on boot is what carries it the rest
 * of the way, not a loop here re-trying against a server that is already
 * answering.
 */
function shouldRetryWrite(failureCount: number, error: Error): boolean {
  if (failureCount > 0) return false
  const status = (error as Error & { status?: number }).status
  return !status || status >= 500
}

/**
 * Registers one write's `mutationFn` under a key stable enough to survive a
 * reload, and hands back the options a `useMutation` call spreads in.
 *
 * Called once per write, at module load — `setMutationDefaults` is keyed
 * storage on the `QueryClient` itself, not a per-render option, and calling
 * it from inside a hook body would just overwrite the same entry on every
 * render for no reason. See this file's own top note for why the function
 * has to be registered here at all, rather than living only in the
 * `useMutation` call below.
 */
function durableWrite<TVars, TData>(
  name: string,
  mutationFn: (vars: TVars) => Promise<TData>,
) {
  const mutationKey = ['write', name] as const
  const options = { mutationKey, mutationFn, retry: shouldRetryWrite }
  queryClient.setMutationDefaults(mutationKey, options)
  return options
}

/** A client-side id for a row that does not have a server one yet. Only ever
 *  lives as long as the insert takes: the key is invalidated on settle and
 *  the real row replaces it. */
const tempId = () =>
  (globalThis.crypto?.randomUUID?.() ?? `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`)

/**
 * Replace one query's cached data, handing back the undo.
 *
 * The undo closes over the previous value itself, not over a description of
 * the change. A query that has never loaded is left alone — there is nothing
 * on screen to patch and writing into it would invent a partial cache the
 * first real fetch then has to correct.
 */
function patch<T>(qc: QueryClient, key: readonly unknown[], fn: (prev: T) => T): () => void {
  const prev = qc.getQueryData<T>(key)
  if (prev === undefined) return () => {}
  qc.setQueryData<T>(key, fn(prev))
  return () => { qc.setQueryData<T>(key, prev) }
}

/** Several patches, undone together and in one go. */
const undoAll = (undos: (() => void)[]) => () => { for (const u of undos) u() }

type Ctx = { undo: () => void } | undefined

/** Typed `Error` rather than `unknown` on purpose: TanStack infers a
 *  mutation's error type from its `onError` handler, and an `unknown` here
 *  would leave every `m.error` in the app untyped — which is exactly what
 *  `useWriteError` needs to read a message off. */
const rollback = (_e: Error, _v: unknown, ctx: Ctx) => { ctx?.undo() }

/** `rollback`, for the three writes whose row leaves the screen on the tap.
 *  The row coming back a second later is easy to miss, and an Undo toast raised
 *  on the tap ("Saved · in your Shortlist") would still be on screen claiming
 *  something that did not happen: the single toast slot is overwritten here, so
 *  the last thing Doug reads is the truth. `useEntryWriteError` is the banner
 *  that stays. */
const rollbackAndSay = (what: string) => (e: Error, v: unknown, ctx: Ctx) => {
  rollback(e, v, ctx)
  toast({ text: `Couldn't ${what} · put back` })
}

// ---------------------------------------------------------------------------
// What you did with an entry
// ---------------------------------------------------------------------------

const ENTRY_STATE = ['entry_state'] as const

/** The `durableWrite` names above that change what Doug did with an entry. */
const ENTRY_WRITES = new Set(['saveEntry', 'forgetEntry', 'dismissEntry'])

/** Phase E4 (K5): `['affinity']`, invalidated by every write below whose
 *  `db.ts` function also calls `logSignal` — `saveEntry`, `dismissEntry`,
 *  `markWent`, `scheduleEntry`, and `seen.ts`'s own `markSeen`. Unlike the
 *  optimistic patches these mutations already carry, the app holds no local
 *  copy of `taste_affinity` to update in place, so the rule above ("a write
 *  whose optimistic patch is the server's outcome invalidates nothing")
 *  does not apply here — the sum a fresh signal moves is a fact only the
 *  view can compute. `forgetEntry` and `markWrong` write no signal
 *  (`db.ts`'s own notes on each) and do not invalidate this. */
const AFFINITY = ['affinity'] as const

/** Upsert the same rows the server is about to, so the feed, the Saved list
 *  and the tab badge all move on the tap. */
function setEntryState(qc: QueryClient, ids: string[], state: 'saved' | 'dismissed') {
  const at = new Date().toISOString()
  return patch<EntryStateRow[]>(qc, ENTRY_STATE, prev => {
    const wanted = new Set(ids)
    const kept = prev.filter(r => !wanted.has(r.entry_id))
    return [...ids.map(entry_id => ({ entry_id, state, at })), ...kept]
  })
}

function clearEntryState(qc: QueryClient, ids: string[]) {
  const gone = new Set(ids)
  return patch<EntryStateRow[]>(qc, ENTRY_STATE, prev => prev.filter(r => !gone.has(r.entry_id)))
}

const saveEntryWrite = durableWrite('saveEntry', ({ ids, features }: { ids: string[]; features: Features }) =>
  ok(forEachEntry(ids, id => saveEntry(id, features))))

export function useSaveEntry() {
  const qc = useQueryClient()
  return useMutation({
    ...saveEntryWrite,
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: ENTRY_STATE })
      return { undo: setEntryState(qc, ids, 'saved') }
    },
    onError: rollbackAndSay('save'),
    onSettled: () => { void qc.invalidateQueries({ queryKey: AFFINITY }) },
  })
}

const forgetEntryWrite = durableWrite('forgetEntry', ({ ids }: { ids: string[] }) =>
  ok(forEachEntry(ids, id => forgetEntry(id))))

/** Unsaving, and undoing a dismissal. A delete rather than a third state:
 *  "I never decided" and "I decided no" are different things. */
export function useForgetEntry() {
  const qc = useQueryClient()
  return useMutation({
    ...forgetEntryWrite,
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: ENTRY_STATE })
      return { undo: clearEntryState(qc, ids) }
    },
    onError: rollbackAndSay('update that'),
  })
}

const dismissEntryWrite = durableWrite('dismissEntry', ({ ids, features }: { ids: string[]; features: Features }) =>
  ok(forEachEntry(ids, id => dismissEntry(id, features))))

export function useDismissEntry() {
  const qc = useQueryClient()
  return useMutation({
    ...dismissEntryWrite,
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: ENTRY_STATE })
      return { undo: setEntryState(qc, ids, 'dismissed') }
    },
    onError: rollbackAndSay('remove it'),
    onSettled: () => { void qc.invalidateQueries({ queryKey: AFFINITY }) },
  })
}

/** The most recent entry write's error, or nothing — `useWriteError` for
 *  writes Browse does not own. The mutations live on each `EventRow`, and a row
 *  that a save takes out of the list unmounts with its mutation, so a
 *  `useWriteError(...)` in Browse could never see the failure that matters most:
 *  the one whose row has already left. `useMutationState` reads the shared
 *  mutation cache instead, which outlives the row. Latest, not first-found, for
 *  the same reason as `useWriteError`: a later attempt is the one being read. */
export function useEntryWriteError(): string | null {
  const writes = useMutationState({
    filters: { predicate: m => ENTRY_WRITES.has(String(m.options.mutationKey?.[1])) },
    select: m => ({ status: m.state.status, at: m.state.submittedAt, msg: (m.state.error as Error | null)?.message ?? null }),
  })
  let latest: (typeof writes)[number] | null = null
  for (const w of writes) {
    if (w.status === 'idle') continue
    if (!latest || w.at > latest.at) latest = w
  }
  return latest && latest.status === 'error' ? `Your last change did not save: ${latest.msg ?? 'unknown error'}` : null
}

const markWrongWrite = durableWrite('markWrong', ({ id }: { id: string }) => ok(markWrong(id)))

/** Not a dismissal: this says the adapter got it wrong and is health, where a
 *  dismissal says not for me and is taste. The row goes away either way, which
 *  is why the cache patch is the same one — and since WP 2.4 that is the whole
 *  of what this writes. The report itself is an `app_notes` row, so this takes
 *  no features and no recipe: there is no signal left to carry them. */
export function useMarkWrong() {
  const qc = useQueryClient()
  return useMutation({
    ...markWrongWrite,
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: ENTRY_STATE })
      return { undo: setEntryState(qc, [id], 'dismissed') }
    },
    onError: rollback,
  })
}

const restoreDismissedWrite = durableWrite('restoreDismissed', () => ok(restoreDismissed()))

export function useRestoreDismissed() {
  const qc = useQueryClient()
  return useMutation({
    ...restoreDismissedWrite,
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ENTRY_STATE })
      return {
        undo: patch<EntryStateRow[]>(qc, ENTRY_STATE, prev => prev.filter(r => r.state !== 'dismissed')),
      }
    },
    onError: rollback,
  })
}

const markWentWrite = durableWrite('markWent',
  ({ entryId, went, features }: { entryId: string; went: boolean; features: Features }) =>
    ok(markWent(entryId, went, features)))

/**
 * WP 4.3, the Shortlist's Went tab: an explicit tick, never inferred from
 * time passing (rule 1). There is nothing to patch optimistically — a tick's
 * own state lives on the screen that drew the checkbox, not in a cache this
 * file owns — so this is the write and `useWriteError` alone, the same shape
 * `useAddNote` has for the same reason.
 */
export function useMarkWent() {
  const qc = useQueryClient()
  return useMutation({
    ...markWentWrite,
    onSettled: () => { void qc.invalidateQueries({ queryKey: AFFINITY }) },
  })
}

// ---------------------------------------------------------------------------
// U1.2: editing the profile
// ---------------------------------------------------------------------------

const updateProfileWrite = durableWrite('updateProfile',
  ({ uid, patch: p }: { uid: string; patch: ProfilePatch }) => ok(updateProfile(uid, p)))

/**
 * `mutate(patch)` for the signed-in person's own profile (U1.4's onboarding
 * and the Account form call it). The patch **is** the server's outcome, so
 * the cached row takes it on the tap and nothing is invalidated — the same
 * rule every write above follows; Near me, the language gate and the lines
 * all move without a round trip. `uid` rides in the variables, not read at
 * run time, so a write queued offline replays against the account that made
 * it (U0.4 drops a sign-out's queue; this keeps a rebuilt one honest).
 *
 * Returns `mutate` shaped as `(patch) => void`, because the caller has no use
 * for `uid` and the key is not the caller's to know.
 */
export function useUpdateProfile() {
  const qc = useQueryClient()
  const { session } = useAuth()
  const uid = session?.user.id
  const m = useMutation({
    ...updateProfileWrite,
    onMutate: async ({ uid: id, patch: p }) => {
      const key = profileKey(id)
      await qc.cancelQueries({ queryKey: key })
      return {
        undo: patch<Profile | null>(qc, key, prev => (prev ? { ...prev, ...p } as Profile : prev)),
      }
    },
    onError: rollback,
  })
  return {
    ...m,
    updateProfile: (p: ProfilePatch) => { if (uid) m.mutate({ uid, patch: p }) },
  }
}

// ---------------------------------------------------------------------------
// U2.4: saving the taste questions
// ---------------------------------------------------------------------------

const saveTasteWrite = durableWrite('saveTasteVector',
  ({ vector }: { vector: VectorJson }) => ok(setTasteVector(vector)))

/**
 * `mutate(vector)` / `mutateAsync(vector)` for the signed-in person's own
 * taste. The vector **is** the server's outcome (the RPC stores exactly it), so
 * the cached `['taste_active', uid]` takes it on the tap and nothing is
 * invalidated. A failure puts the old value back, or drops the key if it had
 * never loaded: an undo to `undefined` would leave the optimistic value standing.
 *
 * Onboarding awaits it (`mutateAsync`) before writing `onboarded_at`, so a
 * save the server refused leaves the person on the step, with a message, not
 * in the app with nothing saved.
 */
export function useSaveTaste() {
  const qc = useQueryClient()
  const { session } = useAuth()
  const uid = session?.user.id
  return useMutation({
    ...saveTasteWrite,
    onMutate: async ({ vector }) => {
      const key = activeTasteKey(uid)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<ActiveTaste>(key)
      const next: ActiveTaste = { version: vector.version, vector }
      qc.setQueryData<ActiveTaste>(key, next)
      return { undo: () => { if (prev === undefined) qc.removeQueries({ queryKey: key }); else qc.setQueryData<ActiveTaste>(key, prev) } }
    },
    onError: rollback,
  })
}

// ---------------------------------------------------------------------------
// WP 3.4b (P3): following a place
// ---------------------------------------------------------------------------

const FOLLOWS = ['follows'] as const

const followVenueWrite = durableWrite('followVenue', (venueId: string) => ok(followVenue(venueId)))

export function useFollowVenue() {
  const qc = useQueryClient()
  return useMutation({
    ...followVenueWrite,
    onMutate: async venueId => {
      await qc.cancelQueries({ queryKey: FOLLOWS })
      return { undo: patch<Set<string>>(qc, FOLLOWS, prev => new Set(prev).add(venueId)) }
    },
    onError: rollback,
  })
}

const unfollowVenueWrite = durableWrite('unfollowVenue', (venueId: string) => ok(unfollowVenue(venueId)))

export function useUnfollowVenue() {
  const qc = useQueryClient()
  return useMutation({
    ...unfollowVenueWrite,
    onMutate: async venueId => {
      await qc.cancelQueries({ queryKey: FOLLOWS })
      return {
        undo: patch<Set<string>>(qc, FOLLOWS, prev => {
          const next = new Set(prev)
          next.delete(venueId)
          return next
        }),
      }
    },
    onError: rollback,
  })
}

// ---------------------------------------------------------------------------
// The year: commitments, stays, flights
// ---------------------------------------------------------------------------

const COMMITMENTS = ['commitments'] as const
const STAYS = ['stays'] as const
const FLIGHTS = ['flights'] as const
const VISITORS = ['visitors'] as const

/** Kept sorted on the column the query orders by, so an optimistic row lands
 *  where the refetched one will. */
const byDate = <T extends { on_date: string }>(list: T[]) =>
  [...list].sort((a, b) => (a.on_date < b.on_date ? -1 : a.on_date > b.on_date ? 1 : 0))

/**
 * The row the server is about to write, spelled out rather than cast.
 *
 * Every field, including the three nothing on screen reads — `note`,
 * `source`, `google_event_id`. A cast would compile with them missing and the
 * row would be `Commitment`-shaped to the type checker and not to anything
 * that walked it, which is the shape of bug this codebase keeps paying for.
 * `source: 'manual'` is what `addCommitment`'s own insert defaults to: this
 * came from the Year screen, not from the nightly Google sync.
 */
const optimisticCommitment = (
  row: { on_date: string; title: string; entry_id: string | null; starts_at?: string | null },
): Commitment => ({
  id: tempId(),
  note: null,
  source: 'manual',
  google_event_id: null,
  is_birthday: false,
  starts_at: null,
  ...row,
})

const scheduleEntryWrite = durableWrite('scheduleEntry',
  ({ entryId, startsAt, title, features }: {
    entryId: string; startsAt: string; title: string; features: Features
  }) => ok(scheduleEntry(entryId, startsAt, title, features)))

/** Putting a saved event in the diary, from a showing's exact `starts_at`
 *  (WP 3.3) — a chip's pick, or `midnightIsoIn(day, hereZone())` for a run with
 *  no clocked showing, `on_date` read in the same zone (`db.ts`'s
 *  `scheduleEntry`). The commitment carries `entry_id`, so the day on the timeline can
 *  open the event it came from. */
export function useScheduleEntry() {
  const qc = useQueryClient()
  return useMutation({
    ...scheduleEntryWrite,
    onMutate: async ({ entryId, startsAt, title }) => {
      await qc.cancelQueries({ queryKey: COMMITMENTS })
      return {
        undo: patch<Commitment[]>(qc, COMMITMENTS, prev => byDate([
          ...prev,
          optimisticCommitment({ on_date: dayIn(startsAt, hereZone()), title, entry_id: entryId, starts_at: startsAt }),
        ])),
      }
    },
    onError: rollback,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COMMITMENTS })
      void qc.invalidateQueries({ queryKey: AFFINITY })
    },
  })
}

const addCommitmentWrite = durableWrite('addCommitment', ({ date, title }: { date: string; title: string }) =>
  ok(addCommitment(date, title)))

export function useAddCommitment() {
  const qc = useQueryClient()
  return useMutation({
    ...addCommitmentWrite,
    onMutate: async ({ date, title }) => {
      await qc.cancelQueries({ queryKey: COMMITMENTS })
      return {
        undo: patch<Commitment[]>(qc, COMMITMENTS, prev => byDate([
          ...prev,
          optimisticCommitment({ on_date: date, title, entry_id: null }),
        ])),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: COMMITMENTS }) },
  })
}

const updateCommitmentWrite = durableWrite('updateCommitment',
  ({ id, patch: p }: { id: string; patch: { on_date: string; title: string } }) =>
    ok(updateCommitment(id, p)))

export function useUpdateCommitment() {
  const qc = useQueryClient()
  return useMutation({
    ...updateCommitmentWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: COMMITMENTS })
      return {
        undo: patch<Commitment[]>(qc, COMMITMENTS, prev =>
          byDate(prev.map(c => (c.id === id ? { ...c, ...p } : c)))),
      }
    },
    onError: rollback,
  })
}

const removeCommitmentWrite = durableWrite('removeCommitment', (id: string) => ok(removeCommitment(id)))

export function useRemoveCommitment() {
  const qc = useQueryClient()
  return useMutation({
    ...removeCommitmentWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: COMMITMENTS })
      return { undo: patch<Commitment[]>(qc, COMMITMENTS, prev => prev.filter(c => c.id !== id)) }
    },
    onError: rollback,
  })
}

type StayInput = {
  kind: StayKind; starts_on: string; ends_on: string
  label: string | null; provisional: boolean; country: string | null
}

const byStart = (list: Stay[]) =>
  [...list].sort((a, b) => (a.starts_on < b.starts_on ? -1 : a.starts_on > b.starts_on ? 1 : 0))

const addStayWrite = durableWrite('addStay', (s: StayInput) => ok(addStay(s)))

export function useAddStay() {
  const qc = useQueryClient()
  return useMutation({
    ...addStayWrite,
    onMutate: async s => {
      await qc.cancelQueries({ queryKey: STAYS })
      return {
        // No cast: `StayInput` is every column of `Stay` but the id.
        undo: patch<Stay[]>(qc, STAYS, prev => byStart([...prev, { id: tempId(), ...s }])),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: STAYS }) },
  })
}

const updateStayWrite = durableWrite('updateStay', ({ id, patch: p }: { id: string; patch: StayInput }) =>
  ok(updateStay(id, p)))

export function useUpdateStay() {
  const qc = useQueryClient()
  return useMutation({
    ...updateStayWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: STAYS })
      return {
        undo: patch<Stay[]>(qc, STAYS, prev => byStart(prev.map(s => (s.id === id ? { ...s, ...p } : s)))),
      }
    },
    onError: rollback,
  })
}

const removeStayWrite = durableWrite('removeStay', (id: string) => ok(removeStay(id)))

export function useRemoveStay() {
  const qc = useQueryClient()
  return useMutation({
    ...removeStayWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: STAYS })
      return { undo: patch<Stay[]>(qc, STAYS, prev => prev.filter(s => s.id !== id)) }
    },
    onError: rollback,
  })
}

/** `note` only from Undo (a2-24), putting back a visitor exactly as removed;
 *  no form writes one. */
type VisitorInput = { who: string; starts_on: string; ends_on: string; at_mine: boolean; note?: string | null }

const byVisitorStart = (list: Visitor[]) =>
  [...list].sort((a, b) => (a.starts_on < b.starts_on ? -1 : a.starts_on > b.starts_on ? 1 : 0))

const addVisitorWrite = durableWrite('addVisitor', (v: VisitorInput) => ok(addVisitor(v)))

export function useAddVisitor() {
  const qc = useQueryClient()
  return useMutation({
    ...addVisitorWrite,
    onMutate: async v => {
      await qc.cancelQueries({ queryKey: VISITORS })
      return {
        // No cast: `VisitorInput` is every column of `Visitor` but the id (and a note, optionally).
        undo: patch<Visitor[]>(qc, VISITORS, prev => byVisitorStart([...prev, { id: tempId(), note: null, ...v }])),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: VISITORS }) },
  })
}

const updateVisitorWrite = durableWrite('updateVisitor', ({ id, patch: p }: { id: string; patch: VisitorInput }) =>
  ok(updateVisitor(id, p)))

export function useUpdateVisitor() {
  const qc = useQueryClient()
  return useMutation({
    ...updateVisitorWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: VISITORS })
      return {
        undo: patch<Visitor[]>(qc, VISITORS, prev => byVisitorStart(prev.map(v => (v.id === id ? { ...v, ...p } : v)))),
      }
    },
    onError: rollback,
  })
}

const removeVisitorWrite = durableWrite('removeVisitor', (id: string) => ok(removeVisitor(id)))

export function useRemoveVisitor() {
  const qc = useQueryClient()
  return useMutation({
    ...removeVisitorWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: VISITORS })
      return { undo: patch<Visitor[]>(qc, VISITORS, prev => prev.filter(v => v.id !== id)) }
    },
    onError: rollback,
  })
}

type FlightInput = {
  on_date: string; depart_at: string | null
  from_iata: string | null; to_iata: string | null; code: string
}

const addFlightWrite = durableWrite('addFlight', (f: FlightInput) => ok(addFlight(f)))

export function useAddFlight() {
  const qc = useQueryClient()
  return useMutation({
    ...addFlightWrite,
    onMutate: async f => {
      await qc.cancelQueries({ queryKey: FLIGHTS })
      return {
        // No cast, and `source: 'manual'` because that is what `addFlight`
        // writes — 'email' is only ever a forwarded itinerary.
        undo: patch<Flight[]>(qc, FLIGHTS, prev =>
          byDate([...prev, { id: tempId(), source: 'manual', ...f }])),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: FLIGHTS }) },
  })
}

const updateFlightWrite = durableWrite('updateFlight', ({ id, patch: p }: { id: string; patch: FlightInput }) =>
  ok(updateFlight(id, p)))

export function useUpdateFlight() {
  const qc = useQueryClient()
  return useMutation({
    ...updateFlightWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: FLIGHTS })
      return {
        undo: patch<Flight[]>(qc, FLIGHTS, prev =>
          byDate(prev.map(f => (f.id === id ? { ...f, ...p } : f)))),
      }
    },
    onError: rollback,
  })
}

const removeFlightWrite = durableWrite('removeFlight', (id: string) => ok(removeFlight(id)))

export function useRemoveFlight() {
  const qc = useQueryClient()
  return useMutation({
    ...removeFlightWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: FLIGHTS })
      return { undo: patch<Flight[]>(qc, FLIGHTS, prev => prev.filter(f => f.id !== id)) }
    },
    onError: rollback,
  })
}

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

const LISTS = ['lists'] as const
const LIST_ITEMS = ['list_items'] as const

const byPosition = <T extends { position: number }>(list: T[]) =>
  [...list].sort((a, b) => a.position - b.position)

const addListWrite = durableWrite('addList',
  ({ name, kind, flagLabel }: { name: string; kind: ListKind; flagLabel?: string | null }) =>
    ok(addList(name, kind, flagLabel ?? null)))

export function useAddList() {
  const qc = useQueryClient()
  return useMutation({
    ...addListWrite,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LISTS }) },
  })
}

const reorderListsWrite = durableWrite('reorderLists', (ids: string[]) => ok(reorderLists(ids)))

/** Positions are what the server will hold, exactly, so nothing is refetched
 *  to confirm them. */
export function useReorderLists() {
  const qc = useQueryClient()
  return useMutation({
    ...reorderListsWrite,
    onMutate: async ids => {
      await qc.cancelQueries({ queryKey: LISTS })
      const at = new Map(ids.map((id, i) => [id, i]))
      return {
        undo: patch<List[]>(qc, LISTS, prev =>
          byPosition(prev.map(l => (at.has(l.id) ? { ...l, position: at.get(l.id)! } : l)))),
      }
    },
    onError: rollback,
  })
}

const addItemWrite = durableWrite('addItem', ({ listId, body, opts }: {
  listId: string; body: string
  opts?: {
    section?: string | null; parent_id?: string | null; position?: number
    qty?: string | null; due_on?: string | null
    repeat_every?: number | null; repeat_unit?: 'day' | 'week' | null
  }
}) => ok(addItem(listId, body, opts ?? {})))

export function useAddItem() {
  const qc = useQueryClient()
  return useMutation({
    ...addItemWrite,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LIST_ITEMS }) },
  })
}

const addItemsWrite = durableWrite('addItems',
  ({ listId, rows }: { listId: string; rows: { body: string; qty: string | null; position: number }[] }) =>
    ok(addItems(listId, rows)))

/** Package B: the review sheet's one write. Not optimistic — the sheet
 *  closes on settle and the list refetches, and a temp row per line would
 *  only be visible under the sheet for the round trip. */
export function useAddItems() {
  const qc = useQueryClient()
  return useMutation({
    ...addItemsWrite,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LIST_ITEMS }) },
  })
}

const addItemForEntryWrite = durableWrite('addItemForEntry',
  ({ listId, entryId, body }: { listId: string; entryId: string; body: string }) =>
    ok(addItemForEntry(listId, entryId, body)))

/**
 * L8: `AddToListSheet`'s own toggle-on. Optimistic, the same reason
 * `addCommitment` is: the temp row is what the sheet's own tick and the list
 * screen the item lands on both read before the round trip settles, and
 * `onSettled` swaps it for the real one. The caller — only `AddToListSheet` —
 * is what keeps this idempotent (rule 1): it never calls this for a
 * (list, entry) pair its own `list_items` cache already carries.
 */
export function useAddItemForEntry() {
  const qc = useQueryClient()
  return useMutation({
    ...addItemForEntryWrite,
    onMutate: async ({ listId, entryId, body }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      const optimistic: ListItem = {
        id: tempId(), list_id: listId, parent_id: null, body, link: null, section: null,
        position: 0, done_at: null, qty: null, due_on: null, price_cents: null, currency: 'EUR',
        status: null, image_url: null, flagged_at: null, note: null, tags: [], entry_id: entryId,
      }
      return { undo: patch<ListItem[]>(qc, LIST_ITEMS, prev => [...prev, optimistic]) }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LIST_ITEMS }) },
  })
}

const setItemDoneWrite = durableWrite('setItemDone', ({ id, done }: { id: string; done: boolean }) =>
  ok(setItemDone(id, done)))

export function useSetItemDone() {
  const qc = useQueryClient()
  return useMutation({
    ...setItemDoneWrite,
    onMutate: async ({ id, done }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      const done_at = done ? new Date().toISOString() : null
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev =>
          prev.map(i => (i.id === id ? { ...i, done_at } : i))),
      }
    },
    onError: rollback,
  })
}

// ---------------------------------------------------------------------------
// Tasks plan (docs/tasks-plan/README.md): the tick that repeats, Clear, Move
// ---------------------------------------------------------------------------

const completeTaskWrite = durableWrite('completeTask',
  ({ id, nextDueOn }: { id: string; nextDueOn: string | null }) => ok(completeTask(id, nextDueOn)))

/** D6: tick a task. Optimistic on `done_at` only; the spawned row arrives
 *  with the refetch on settle. Untick stays `useSetItemDone`, which also
 *  serves every other kind — `tickTask` below is the one place that
 *  chooses between the two, so neither screen does. */
export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    ...completeTaskWrite,
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      const done_at = new Date().toISOString()
      return { undo: patch<ListItem[]>(qc, LIST_ITEMS, prev => prev.map(i => (i.id === id ? { ...i, done_at } : i))) }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LIST_ITEMS }) },
  })
}

/** The tick, decided once: a repeating task goes through `complete_task`
 *  with its next due day (`nextDue`, D6), every other task and every untick
 *  through `setItemDone`. `today` is the current region's day. */
export function tickTask(
  m: { done: ReturnType<typeof useSetItemDone>; complete: ReturnType<typeof useCompleteTask> },
  item: Pick<ListItem, 'id' | 'done_at' | 'due_on' | 'repeat_every' | 'repeat_unit'>,
  today: string,
): { nextDueOn: string | null } {
  if (item.done_at) { m.done.mutate({ id: item.id, done: false }); return { nextDueOn: null } }
  const every = item.repeat_every ?? null, unit = item.repeat_unit ?? null
  if (every && unit) {
    const nextDueOn = nextDue(item.due_on, today, every, unit)
    m.complete.mutate({ id: item.id, nextDueOn })
    return { nextDueOn }
  }
  m.done.mutate({ id: item.id, done: true })
  return { nextDueOn: null }
}

const clearDoneWrite = durableWrite('clearDone',
  ({ listId, at }: { listId: string; at: string | null }) => ok(clearDone(listId, at)))

/** D3: Clear on the Done fold, optimistic on `['lists']`. Undo passes the
 *  previous value back. */
export function useClearDone() {
  const qc = useQueryClient()
  return useMutation({
    ...clearDoneWrite,
    onMutate: async ({ listId, at }) => {
      await qc.cancelQueries({ queryKey: LISTS })
      return { undo: patch<List[]>(qc, LISTS, prev => prev.map(l => (l.id === listId ? { ...l, done_cleared_at: at } : l))) }
    },
    onError: rollback,
  })
}

const moveLineWrite = durableWrite('moveLine',
  ({ id, toListId }: { id: string; toListId: string; patch: Partial<ListItem> }) => ok(moveLine(id, toListId)))

/** D8: a line to another list. The caller passes `movePatch(from, to, id)`
 *  (`lib/moveLine.ts`) so the cache shows what the server will: the row
 *  and its subtasks on the new list, the columns the kind cannot hold
 *  nulled. The server moves the children; the cache does the same here. */
export function useMoveLine() {
  const qc = useQueryClient()
  return useMutation({
    ...moveLineWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev =>
          prev.map(i => (i.id === id || i.parent_id === id ? { ...i, ...p } : i))),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LIST_ITEMS }) },
  })
}

const updateItemWrite = durableWrite('updateItem',
  ({ id, patch: p }: { id: string; patch: { body?: string; link?: string | null; qty?: string | null } }) =>
    ok(updateItem(id, p)))

export function useUpdateItem() {
  const qc = useQueryClient()
  return useMutation({
    ...updateItemWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev =>
          prev.map(i => (i.id === id ? { ...i, ...p } : i))),
      }
    },
    onError: rollback,
  })
}

const setItemFieldsWrite = durableWrite('setItemFields',
  ({ id, patch: p }: { id: string; patch: ListItemFields }) => ok(setItemFields(id, p)))

/** L4: editing a task's due date, from the small Sheet Rule 1 makes the one
 *  exception for. `ListItemFields` already covers `due_on` — it is one of
 *  the kind-specific columns `updateItem` doesn't. */
export function useSetItemFields() {
  const qc = useQueryClient()
  return useMutation({
    ...setItemFieldsWrite,
    onMutate: async ({ id, patch: p }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev => prev.map(i => (i.id === id ? { ...i, ...p } : i))),
      }
    },
    onError: rollback,
  })
}

const setItemParentWrite = durableWrite('setItemParent',
  ({ id, parent_id }: { id: string; parent_id: string | null }) =>
    ok(supabase.from('list_items').update({ parent_id }).eq('id', id)))

/** L4: a swipe right indents a task under the previous top-level row, or
 *  outdents it if it already has one. Written directly, the same shape
 *  `useUpdateListShop` uses — `updateItem` and `setItemFields` both cover
 *  columns `list_item_kind_guard` gates by kind, and `parent_id` is neither. */
export function useSetItemParent() {
  const qc = useQueryClient()
  return useMutation({
    ...setItemParentWrite,
    onMutate: async ({ id, parent_id }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev => prev.map(i => (i.id === id ? { ...i, parent_id } : i))),
      }
    },
    onError: rollback,
  })
}

const reorderItemsWrite = durableWrite('reorderItems', (ids: string[]) => ok(reorderItems(ids)))

export function useReorderItems() {
  const qc = useQueryClient()
  return useMutation({
    ...reorderItemsWrite,
    onMutate: async ids => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      const at = new Map(ids.map((id, i) => [id, i]))
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev =>
          byPosition(prev.map(i => (at.has(i.id) ? { ...i, position: at.get(i.id)! } : i)))),
      }
    },
    onError: rollback,
  })
}

const removeItemWrite = durableWrite('removeItem', (id: string) => ok(removeItem(id)))

export function useRemoveItem() {
  const qc = useQueryClient()
  return useMutation({
    ...removeItemWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return { undo: patch<ListItem[]>(qc, LIST_ITEMS, prev => prev.filter(i => i.id !== id)) }
    },
    onError: rollback,
  })
}

const updateListShopWrite = durableWrite('updateListShop', ({ id, shop }: { id: string; shop: string | null }) =>
  ok(supabase.from('lists').update({ shop }).eq('id', id)))

/** L3: the shop name on a grocery list's header. Written directly rather
 *  than through a `db.ts` wrapper — the same shape `useUpdateVenue` already
 *  uses for a one-column patch nothing else needs to call. */
export function useUpdateListShop() {
  const qc = useQueryClient()
  return useMutation({
    ...updateListShopWrite,
    onMutate: async ({ id, shop }) => {
      await qc.cancelQueries({ queryKey: LISTS })
      return { undo: patch<List[]>(qc, LISTS, prev => prev.map(l => (l.id === id ? { ...l, shop } : l))) }
    },
    onError: rollback,
  })
}

const renameListWrite = durableWrite('renameList', ({ id, name }: { id: string; name: string }) =>
  ok(renameList(id, name)))

export function useRenameList() {
  const qc = useQueryClient()
  return useMutation({
    ...renameListWrite,
    onMutate: async ({ id, name }) => {
      await qc.cancelQueries({ queryKey: LISTS })
      return { undo: patch<List[]>(qc, LISTS, prev => prev.map(l => (l.id === id ? { ...l, name } : l))) }
    },
    onError: rollback,
  })
}

const pinListWrite = durableWrite('pinList', ({ id, pinned }: { id: string; pinned: boolean }) =>
  ok(pinList(id, pinned)))

export function usePinList() {
  const qc = useQueryClient()
  return useMutation({
    ...pinListWrite,
    onMutate: async ({ id, pinned }) => {
      await qc.cancelQueries({ queryKey: LISTS })
      return { undo: patch<List[]>(qc, LISTS, prev => prev.map(l => (l.id === id ? { ...l, pinned } : l))) }
    },
    onError: rollback,
  })
}

const changeListKindWrite = durableWrite('changeListKind', ({ id, kind }: { id: string; kind: ListKind }) =>
  ok(changeListKind(id, kind)))

export function useChangeListKind() {
  const qc = useQueryClient()
  return useMutation({
    ...changeListKindWrite,
    onMutate: async ({ id, kind }) => {
      await qc.cancelQueries({ queryKey: LISTS })
      return { undo: patch<List[]>(qc, LISTS, prev => prev.map(l => (l.id === id ? { ...l, kind } : l))) }
    },
    onError: rollback,
  })
}

const archiveListWrite = durableWrite('archiveList', ({ id, archived }: { id: string; archived: boolean }) =>
  ok(archiveList(id, archived)))

/** L9: archive and its own undo (restore) are the same write with the flag
 *  flipped — `archiveList`'s own shape. */
export function useArchiveList() {
  const qc = useQueryClient()
  return useMutation({
    ...archiveListWrite,
    onMutate: async ({ id, archived }) => {
      await qc.cancelQueries({ queryKey: LISTS })
      const archived_at = archived ? new Date().toISOString() : null
      return { undo: patch<List[]>(qc, LISTS, prev => prev.map(l => (l.id === id ? { ...l, archived_at } : l))) }
    },
    onError: rollback,
  })
}

const deleteListWrite = durableWrite('deleteList', (id: string) => ok(deleteList(id)))

/** L9: called only once the toast Undo has expired — `ListMenu` archives
 *  first and this is what a spent undo turns into. Cascades to the list's
 *  items, runs and usuals in Postgres, so both caches are patched here. */
export function useDeleteList() {
  const qc = useQueryClient()
  return useMutation({
    ...deleteListWrite,
    onMutate: async id => {
      await Promise.all([qc.cancelQueries({ queryKey: LISTS }), qc.cancelQueries({ queryKey: LIST_ITEMS })])
      return {
        undo: undoAll([
          patch<List[]>(qc, LISTS, prev => prev.filter(l => l.id !== id)),
          patch<ListItem[]>(qc, LIST_ITEMS, prev => prev.filter(i => i.list_id !== id)),
        ]),
      }
    },
    onError: rollback,
  })
}

const resetListWrite = durableWrite('resetList', ({ listId, itemCount }: { listId: string; itemCount: number }) =>
  ok(resetList(listId, itemCount)))

/** A template is reset rather than emptied: the run is logged, then every tick
 *  is cleared and the items survive for the next guest. */
export function useResetList() {
  const qc = useQueryClient()
  return useMutation({
    ...resetListWrite,
    onMutate: async ({ listId }) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev =>
          prev.map(i => (i.list_id === listId ? { ...i, done_at: null } : i))),
      }
    },
    onError: rollback,
  })
}

const LIST_RUNS = ['list_runs'] as const

const newRunWrite = durableWrite('newRun', async (listId: string) => {
  await ok(newRun(listId))
  // `newRun` (`db.ts`) inserts the row and clears every tick in one round
  // trip but returns only the tick-clearing result — it has no caller that
  // ever needed the run's own id before this one. Reading it back by
  // `completed_at desc` rather than widening that return is what lets
  // `Checklist.tsx`'s "New run" Undo delete exactly the row it just made.
  return ok(
    supabase.from('list_runs').select('id').eq('list_id', listId)
      .order('completed_at', { ascending: false }).limit(1).single(),
  )
})

/** L5: start a fresh run of a checklist. */
export function useNewRun() {
  const qc = useQueryClient()
  return useMutation({
    ...newRunWrite,
    onMutate: async (listId: string) => {
      await qc.cancelQueries({ queryKey: LIST_ITEMS })
      return {
        undo: patch<ListItem[]>(qc, LIST_ITEMS, prev =>
          prev.map(i => (i.list_id === listId ? { ...i, done_at: null } : i))),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LIST_RUNS }) },
  })
}

type DuplicateInput = { list: List; items: ListItem[]; name: string }

const duplicateListWrite = durableWrite('duplicateList', async ({ list, items, name }: DuplicateInput) => {
  const created = await ok(
    supabase.from('lists').insert({ name, kind: list.kind, flag_label: list.flag_label }).select('id').single(),
  )
  if (items.length) {
    await ok(supabase.from('list_items').insert(
      items.map(i => ({ list_id: created.data!.id, body: i.body, section: i.section, position: i.position })),
    ))
  }
  return created
})

/** L1's "Duplicate", templates only (`ListMenu.tsx`): copies the list row and
 *  every item's body/section/position. Ticks and run history are not carried
 *  over — the copy starts exactly as fresh as `newRun` above leaves one. */
export function useDuplicateList() {
  const qc = useQueryClient()
  return useMutation({
    ...duplicateListWrite,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: LISTS })
      void qc.invalidateQueries({ queryKey: LIST_ITEMS })
    },
  })
}

type AddListForEntryInput = { name: string; kind: ListKind; entryId: string; body: string }

const addListForEntryWrite = durableWrite('addListForEntry',
  async ({ name, kind, entryId, body }: AddListForEntryInput) => {
    const list = await ok(supabase.from('lists').insert({ name, kind }).select('id').single())
    return ok(supabase.from('list_items').insert({ list_id: list.data!.id, entry_id: entryId, body }))
  })

/** L8's "New list" row in `AddToListSheet`: the same two-insert shape
 *  `duplicateList` above already uses for a list plus its rows, with one
 *  item instead of many. No optimistic patch, unlike `useAddItemForEntry` —
 *  the new list itself has no cached id to hang a temp item off until the
 *  first insert has actually settled, so both land together on invalidate. */
export function useAddListForEntry() {
  const qc = useQueryClient()
  return useMutation({
    ...addListForEntryWrite,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: LISTS })
      void qc.invalidateQueries({ queryKey: LIST_ITEMS })
    },
  })
}

const LIST_USUALS = (listId: string) => ['list_usuals', listId] as const

const bumpUsualWrite = durableWrite('bumpUsual', ({ listId, body }: { listId: string; body: string }) =>
  ok(bumpUsual(listId, body)))

/** L3: fired on every add to a grocery list, whether the text came from the
 *  usuals shelf or was typed fresh. No optimistic patch — `bump_usual`'s own
 *  increment is the fact this is recording and the shelf reordering by one
 *  bump is not worth a snapshot to roll back. */
export function useBumpUsual() {
  const qc = useQueryClient()
  return useMutation({
    ...bumpUsualWrite,
    onSettled: (_d, _e, { listId }) => { void qc.invalidateQueries({ queryKey: LIST_USUALS(listId) }) },
  })
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

const APP_NOTES = ['app_notes'] as const
/** Must match `useWrongReports`'s own key in `queries.ts`. */
const WRONG_REPORTS = ['wrong_reports'] as const

/** The venue admin reads its own venue-scoped slice through `venueNotes.ts`,
 *  so a note written there has two places to land. */
const venueNoteKey = (venueId: string) => ['admin', 'venue_notes', venueId] as const

const addNoteWrite = durableWrite('addNote',
  ({ body, kind, venueId, entryId, createdAt }:
    { body: string; kind: NoteKind; venueId?: string; entryId?: string; createdAt?: string }) =>
    ok(addNote(body, kind, venueId, entryId, createdAt)))

export function useAddNote() {
  const qc = useQueryClient()
  return useMutation({
    ...addNoteWrite,
    onSettled: (_d, _e, { venueId, entryId }) => {
      void qc.invalidateQueries({ queryKey: APP_NOTES })
      if (venueId) void qc.invalidateQueries({ queryKey: venueNoteKey(venueId) })
      // The health strip counts notes about a listing, so a new one has to
      // reach it — the strip is the one place that would otherwise keep
      // showing yesterday's number after a report was just filed.
      if (entryId) void qc.invalidateQueries({ queryKey: WRONG_REPORTS })
    },
  })
}

const editNoteWrite = durableWrite('editNote', ({ id, body }: { id: string; body: string }) =>
  ok(editNote(id, body)))

export function useEditNote() {
  const qc = useQueryClient()
  return useMutation({
    ...editNoteWrite,
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: APP_NOTES })
      return {
        undo: patch<AppNote[]>(qc, APP_NOTES, prev =>
          prev.map(n => (n.id === id ? { ...n, body: body.trim() } : n))),
      }
    },
    onError: rollback,
  })
}

const removeNoteWrite = durableWrite('removeNote', (id: string) => ok(removeNote(id)))

export function useRemoveNote() {
  const qc = useQueryClient()
  return useMutation({
    ...removeNoteWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: APP_NOTES })
      return { undo: patch<AppNote[]>(qc, APP_NOTES, prev => prev.filter(n => n.id !== id)) }
    },
    onError: rollback,
  })
}

const setNoteDoneWrite = durableWrite('setNoteDone', ({ id, done, resolution }: {
  id: string; done: boolean; resolution?: string
  venueId?: string
}) => ok(setNoteDone(id, done, resolution)))

/** Reopening clears the resolution as well as the date — the same rule
 *  `setNoteDone` writes, restated on the cache so the two agree. */
export function useSetNoteDone() {
  const qc = useQueryClient()
  return useMutation({
    ...setNoteDoneWrite,
    onMutate: async ({ id, done, resolution, venueId }) => {
      const keys = venueId ? [APP_NOTES, venueNoteKey(venueId)] : [APP_NOTES]
      await Promise.all(keys.map(k => qc.cancelQueries({ queryKey: k })))
      const next = done
        ? { done_at: new Date().toISOString(), resolution: resolution?.trim() || null }
        : { done_at: null, resolution: null }
      return {
        undo: undoAll(keys.map(k =>
          patch<AppNote[]>(qc, k, prev => prev.map(n => (n.id === id ? { ...n, ...next } : n))))),
      }
    },
    onError: rollback,
  })
}

// ---------------------------------------------------------------------------
// Leads and health
// ---------------------------------------------------------------------------

const LEADS = ['leads'] as const
const ALERTS = ['alerts'] as const

const acknowledgeAlertWrite = durableWrite('acknowledgeAlert', (id: string) => ok(acknowledgeAlert(id)))

/** Tapping an alert acknowledges it, and the query only ever holds the
 *  unacknowledged ones — so the patch is a removal, not a field. */
export function useAcknowledgeAlert() {
  const qc = useQueryClient()
  return useMutation({
    ...acknowledgeAlertWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: ALERTS })
      return { undo: patch<{ id: string }[]>(qc, ALERTS, prev => prev.filter(a => a.id !== id)) }
    },
    onError: rollback,
  })
}

const dismissLeadWrite = durableWrite('dismissLead', (id: string) => ok(dismissLead(id)))

export function useDismissLead() {
  const qc = useQueryClient()
  return useMutation({
    ...dismissLeadWrite,
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: LEADS })
      return { undo: patch<Lead[]>(qc, LEADS, prev => prev.filter(l => l.id !== id)) }
    },
    onError: rollback,
  })
}

const promoteLeadWrite = durableWrite('promoteLead',
  ({ lead, type, venueId }: { lead: Lead; type: string; venueId: string | null }) =>
    ok(promoteLead(lead, type, venueId)))

/**
 * Promoting a lead is the one write that genuinely changes the feed: it
 * inserts an `entries` row and its showings. So this is the only mutation that
 * invalidates `['feed']`, and it is an admin action taken a handful of times a
 * week rather than something on the tap path.
 */
export function usePromoteLead() {
  const qc = useQueryClient()
  return useMutation({
    ...promoteLeadWrite,
    onMutate: async ({ lead }) => {
      await qc.cancelQueries({ queryKey: LEADS })
      return { undo: patch<Lead[]>(qc, LEADS, prev => prev.filter(l => l.id !== lead.id)) }
    },
    onError: rollback,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: LEADS })
      void qc.invalidateQueries({ queryKey: ['feed'] })
      void qc.invalidateQueries({ queryKey: ['showings'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Venue admin
// ---------------------------------------------------------------------------

type VenuePatch = {
  name: string
  kind: string
  city: string | null
  site_url: string | null
  commercial: boolean
  hero_credit: string | null
  hero_license: string | null
  hero_alt: string | null
  /** A date or null — the grant is per column (20261002010000), the same as
   *  the rest of this patch. */
  closed_at: string | null
}

const updateVenueWrite = durableWrite('updateVenue', ({ id, patch: p }: { id: string; patch: VenuePatch }) =>
  ok(supabase.from('venues').update(p).eq('id', id)))

/**
 * Metadata only. `hero_key` is never in this update — the Worker already wrote
 * it with the service role, and the browser's own grant on `venues` does not
 * include that column (migration 20260921240000).
 *
 * Both venue keys are patched: the five tab screens read `['venues']` and this
 * sheet reads `['admin','venues']`, and a rename that shows in one and not the
 * other is the kind of split this package exists to close.
 */
export function useUpdateVenue() {
  const qc = useQueryClient()
  return useMutation({
    ...updateVenueWrite,
    onMutate: async ({ id, patch: p }) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['admin', 'venues'] }),
        qc.cancelQueries({ queryKey: ['venues'] }),
      ])
      return {
        undo: undoAll([
          patch<AdminVenue[]>(qc, ['admin', 'venues'], prev =>
            prev.map(v => (v.id === id ? { ...v, ...p } : v))),
          patch<{ id: string }[]>(qc, ['venues'], prev =>
            prev.map(v => (v.id === id ? { ...v, ...p } : v))),
        ]),
      }
    },
    onError: rollback,
  })
}

/** The hero upload writes `venues.hero_key` through the Worker rather than
 *  through PostgREST, so there is no mutation to hang the patch off — the
 *  editor calls this once the upload has come back with a key. */
export function useHeroSaved() {
  const qc = useQueryClient()
  return (id: string, heroKey: string) => {
    patch<AdminVenue[]>(qc, ['admin', 'venues'], prev =>
      prev.map(v => (v.id === id ? { ...v, hero_key: heroKey } : v)))
    void qc.invalidateQueries({ queryKey: ['venues'] })
  }
}

// ---------------------------------------------------------------------------
// Leave, as marked days
// ---------------------------------------------------------------------------

const LEAVE_DAYS = ['leave_days'] as const

/** Idempotent both ways — an upsert on the primary key and a delete by it —
 *  so a retried write after going offline cannot double-mark or fail. */
const setLeaveDayWrite = durableWrite('setLeaveDay', ({ day, on }: { day: string; on: boolean }) =>
  ok(on
    ? supabase.from('leave_days').upsert({ day }, { onConflict: 'user_id,day', ignoreDuplicates: true })
    : supabase.from('leave_days').delete().eq('day', day)))

/** Mark or clear one day. The patch is the server's outcome, but the key is
 *  invalidated on settle all the same: the set is small and cheap to re-read. */
export function useSetLeaveDay() {
  const qc = useQueryClient()
  return useMutation({
    ...setLeaveDayWrite,
    onMutate: async ({ day, on }) => {
      await qc.cancelQueries({ queryKey: LEAVE_DAYS })
      return {
        undo: patch<LeaveDay[]>(qc, LEAVE_DAYS, prev => {
          const rest = prev.filter(d => d.day !== day)
          return on ? [...rest, { day }].sort((a, b) => (a.day < b.day ? -1 : 1)) : rest
        }),
      }
    },
    onError: rollback,
    onSettled: () => { void qc.invalidateQueries({ queryKey: LEAVE_DAYS }) },
  })
}
